<template>
  <div class="call-fb-card-container">
    <BUserTooltip
      :image-url="callFeedback.operator.avatarUrl"
      :name="callFeedback.operator.name"
      :user-id="callFeedback.operator.id"
      :size="40"
    />
    <div class="call-fb-card-detail">
      <div class="call-fb-card-detail-header">
        <div class="call-fb-card-detail-header-left">
          <div class="call-fb-card-detail-header-base-data">
            <div class="call-fb-card-detail-header-item">
              <LeadLink
                :call-target-id="callFeedback.callTarget.id"
              >
                {{ callFeedback.callTarget.fullName }}
              </LeadLink>
            </div>
            <div class="call-fb-card-detail-header-item">
              {{ callFeedback.callTarget.companyName }}
            </div>
          </div>
          <div class="call-fb-card-detail-header-detail-data">
            <div class="call-fb-card-detail-header-item">
              <span class="call-fb-card-detail-item-label">{{ `${i18n.t('training.callFb.callDateTime')}：` }}</span>{{ callDateTime }}
            </div>
            <div class="call-fb-card-detail-header-item">
              <span class="call-fb-card-detail-item-label">{{ `${i18n.t('training.callFb.connectedTo')}：` }}</span>{{ connectedToText(callFeedback.connectedTo) }}
            </div>
            <div class="call-fb-card-detail-header-item">
              <span class="call-fb-card-detail-item-label">{{ `${i18n.t('training.callFb.callResultType')}：` }}</span>{{ callFeedback.callResultTypeText }}
            </div>
          </div>
        </div>
        <div class="call-fb-card-detail-header-right">
          <CallResultDetailLink :call-result-id="callFeedback.callResultId" />
        </div>
      </div>
      <div class="call-fb-card-detail-content">
        <div class="talk-time">
          <div class="call-fb-card-detail-item-label">
            {{ i18n.t('training.callFb.talkTime.label') }}
          </div>
          <div class="talk-time-value">
            <div class="talk-time-value-inner">
              <span>{{ talkTime }}</span>
              <span class="unit">{{ i18n.t('training.callFb.talkTime.unit') }}</span>
            </div>
          </div>
        </div>
        <CallFbCardRatingItem
          :unit="i18n.t('training.callFb.talkRatio.unit')"
          :label="i18n.t('training.callFb.talkRatio.label')"
          :main-value="insideSalesTalkRatio"
          :main-value-percentage="insideSalesTalkRatio"
          :main-value-user="callFeedback.operator"
          :sub-value="customerTalkRatio"
          :sub-value-percentage="Number(customerTalkRatio)"
          :rating="talkRatioRating"
        />
        <CallFbCardRatingItem
          :unit="i18n.t('training.callFb.talkSpeed.unit')"
          :label="i18n.t('training.callFb.talkSpeed.label')"
          :main-value="insideSalesTalkSpeed"
          :main-value-percentage="talkSpeedPercentage"
          :main-value-user="callFeedback.operator"
          :sub-value="Math.round(callFeedback.moraPerSecond.customer)"
          :sub-value-percentage="Number(callFeedback.moraPerSecond.customer)"
          :rating="talkSpeedRating"
        />
        <CallFbCardCountItem
          :label="i18n.t('training.callFb.questionCount.label')"
          :value="callFeedback.questionCount"
          :percentage="questionCountPercentage"
          :rating="questionCountRating"
          :max-value="MAX_QUESTION_COUNT"
        />
        <CallFbCardCountItem
          :label="i18n.t('training.callFb.closingCount.label')"
          :value="callFeedback.closingCount"
          :percentage="closingCountPercentage"
          :rating="closingCountRating"
          :max-value="MAX_CLOSING_COUNT"
        />
        <div class="call-fb-card-ai-feedback">
          <BBtn
            v-if="callFeedback?.aiFeedbackResult"
            type="primary"
            @click="handleOpenAiFeedbackModal"
          >
            {{ $t('training.callFb.aiFeedbackResult.title') }}
          </BBtn>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { CallFeedback } from '@/api/openapi';
import LeadLink from '@/components/organisms/user/general/LeadLink.vue';
import { formatDateTimeRange } from '@/utils/date-time';
import CallResultDetailLink from '../general/CallResultDetailLink.vue';
import CallFbCardCountItem from './CallFbCardCountItem.vue';
import CallFbCardRatingItem from './CallFbCardRatingItem.vue';
import type { TRating } from './types';

type TProps = {
  callFeedback: CallFeedback;
};

const props = defineProps<TProps>();
const emit = defineEmits(['openAiFeedbackModal']);

const TALK_SPEED_PERCENTAGE_BAR_MAX_VALUE = 20;
const MAX_QUESTION_COUNT = 15;
const MAX_CLOSING_COUNT = 5;

const i18n = useI18n();

// 会話時間 ---
const callDateTime = computed<string>(() => {
  return formatDateTimeRange(props.callFeedback.startedAt, props.callFeedback.endedAt);
});
const talkTime = computed<string>(() => {
  return `${Math.floor(props.callFeedback.totalTalkSeconds / 60)}:${Math.floor(props.callFeedback.totalTalkSeconds % 60)}`;
});

// 定量評価 ---
const insideSalesTalkRatio = computed<number>(() => {
  // NOTE: 100を掛けた時に誤差が発生する場合があるので、四捨五入して誤差を消す。
  return Math.round((props.callFeedback.talkRatio.insideSales || 0) * 100);
});
const customerTalkRatio = computed<number>(() => {
  // NOTE: 100を掛けた時に誤差が発生する場合があるので、四捨五入して誤差を消す。
  return Math.round((props.callFeedback.talkRatio.customer || 0) * 100);
});
const talkRatioRating = computed<TRating>(() => {
  if (insideSalesTalkRatio.value >= 60 && insideSalesTalkRatio.value < 70) {
    return 'good';
  } else if (insideSalesTalkRatio.value >= 90 || insideSalesTalkRatio.value < 40) {
    return 'fail';
  } else {
    return 'passable';
  }
});
const insideSalesTalkSpeed = computed<number>(() => {
  return Math.round(props.callFeedback.moraPerSecond.insideSales);
});
const talkSpeedPercentage = computed<number>(() => {
  return Math.min(insideSalesTalkSpeed.value / TALK_SPEED_PERCENTAGE_BAR_MAX_VALUE * 100, 100);
});
const talkSpeedRating = computed<TRating>(() => {
  if (insideSalesTalkSpeed.value >= 10) {
    return 'good';
  } else if (insideSalesTalkSpeed.value < 7) {
    return 'fail';
  } else {
    return 'passable';
  }
});

// 定性評価 ---
const calcQualitativePercentage = (count: number | null, maxValue: number) => {
  if (count === null) return 0;
  return Math.min(count / maxValue * 100, 100);
};
const questionCountPercentage = computed<number>(() => {
  return calcQualitativePercentage(props.callFeedback.questionCount, MAX_QUESTION_COUNT);
});
const questionCountRating = computed<TRating>(() => {
  if (props.callFeedback.questionCount == null) return 'none';
  if (props.callFeedback.questionCount >= 5 && props.callFeedback.questionCount < 10) {
    return 'good';
  } else if (props.callFeedback.questionCount === 0 || props.callFeedback.questionCount >= 10) {
    return 'fail';
  } else {
    return 'passable';
  }
});
const closingCountPercentage = computed<number>(() => {
  return calcQualitativePercentage(props.callFeedback.closingCount, MAX_CLOSING_COUNT);
});
const closingCountRating = computed<TRating>(() => {
  if (props.callFeedback.closingCount == null) return 'none';
  if (props.callFeedback.closingCount >= 1 && props.callFeedback.closingCount < 3) {
    return 'good';
  } else {
    return 'fail';
  }
});

const connectedToText = (connectedTo: string) => {
  if (connectedTo === 'unknown') {
    return i18n.t('callResult.connectedTo.enum.unknown');
  } else if (connectedTo === 'not_stakeholder') {
    return i18n.t('callResult.connectedTo.enum.notStakeholder');
  } else if (connectedTo === 'stakeholder') {
    return i18n.t('callResult.connectedTo.enum.stakeholder');
  }
  return '';
};

const handleOpenAiFeedbackModal = () => {
  emit('openAiFeedbackModal', props.callFeedback);
};
</script>

<style lang="scss" scoped>
.call-fb-card-container {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: $basespace-300;
  width: 100%;
  padding: $basespace-500 $basespace-500;
  border-bottom: 1px solid $bdcolor-base;
}

.call-fb-card-detail {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  max-width: 100%;
  min-width: 892px;
}

.call-fb-card-detail-header {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  gap: $basespace-500;
  width: 100%;
  padding-bottom: $basespace-200;
  border-bottom: 1px solid $bdcolor-light;
}

.call-fb-card-detail-header-left {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: $basespace-100 $basespace-300;
  flex-wrap: wrap;
  overflow: hidden;
  width: 100%;
}

.call-fb-card-detail-header-base-data {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: $basespace-100;
  overflow: hidden;
}

.call-fb-card-detail-header-detail-data {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: $basespace-200;
  overflow: hidden;
}

.call-fb-card-detail-header-right {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-end;
  flex-shrink: 0;
}

.call-fb-card-detail-header-item {
  @include m-truncate-text;
}

.call-fb-card-detail-item-label {
  color: $textcolor-light;
  padding-bottom: $basespace-50;
}

.call-fb-card-detail-content {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
  gap: $basespace-500;
  padding-top: $basespace-200;
}

.call-fb-card-ai-feedback {
  display: flex;
  flex: 1;
  min-width: 152px;
  align-items: center;
}

.talk-time {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 124px;
  gap: $basespace-50;

  .talk-time-value {
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: $fontsize-400;
    font-weight: 700;

    .talk-time-value-inner {
      display: flex;
      flex-direction: row;
      align-items: flex-end;
      .unit {
        font-size: $fontsize-100;
        line-height: calc($fontsize-400 - 4px); // テキスト位置を下揃えにする
      }
    }
  }
}

.call-fb-card-rating-item-container, .call-fb-card-count-item-container {
  flex: 1;
  min-width: 124px;
}
</style>
